import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('minnow_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Generic HTTP methods for admin and flexible API calls
  get: (url) => axios.get(`${API}${url}`, { headers: getAuthHeader() }),
  post: (url, data) => axios.post(`${API}${url}`, data, { headers: getAuthHeader() }),
  put: (url, data) => axios.put(`${API}${url}`, data, { headers: getAuthHeader() }),
  delete: (url) => axios.delete(`${API}${url}`, { headers: getAuthHeader() }),
  patch: (url, data) => axios.patch(`${API}${url}`, data, { headers: getAuthHeader() }),

  // Auth
  login: (email, password) => 
    axios.post(`${API}/auth/login`, { email, password }),
  
  register: (email, password, name, startTrial = true) => 
    axios.post(`${API}/auth/register`, { email, password, name, start_trial: startTrial }),
  
  skipTrial: () =>
    axios.post(`${API}/skip-trial`, {}, { headers: getAuthHeader() }),
  
  getMe: () => 
    axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),

  // Onboarding
  completeOnboarding: (data) => 
    axios.post(`${API}/onboarding`, data, { headers: getAuthHeader() }),

  // Curriculum
  getCurriculum: () => 
    axios.get(`${API}/curriculum`, { headers: getAuthHeader() }),
  
  getModule: (moduleId) => 
    axios.get(`${API}/curriculum/${moduleId}`, { headers: getAuthHeader() }),
  
  generateLesson: (moduleId, lessonId) => 
    axios.post(`${API}/lesson/generate`, { module_id: moduleId, lesson_id: lessonId }, { headers: getAuthHeader(), timeout: 60000 }),

  // Chat
  sendMessage: (roomType, roomId, content, translate = false) => 
    axios.post(`${API}/chat/message`, { room_type: roomType, room_id: roomId, content, translate }, { headers: getAuthHeader() }),
  
  getMessages: (roomType, roomId, autoTranslate = true) => 
    axios.get(`${API}/chat/${roomType}/${roomId}?auto_translate=${autoTranslate}`, { headers: getAuthHeader() }),

  // Settings
  updateSettings: (settings) =>
    axios.post(`${API}/users/settings`, settings, { headers: getAuthHeader() }),

  // Minnow Room
  minnowChat: (message) => 
    axios.post(`${API}/minnow/chat`, { message }, { headers: getAuthHeader(), timeout: 60000 }),

  // Weekly Plan
  generateWeeklyPlan: (timeAvailable) => 
    axios.post(`${API}/weekly-plan/generate`, { time_available_hours: timeAvailable }, { headers: getAuthHeader(), timeout: 60000 }),
  
  getCurrentPlan: () => 
    axios.get(`${API}/weekly-plan/current`, { headers: getAuthHeader() }),

  // Readiness Score
  getReadinessScore: () => 
    axios.get(`${API}/readiness-score`, { headers: getAuthHeader() }),

  // Insights
  generateInsight: (context, insightType) => 
    axios.post(`${API}/insights/generate`, { context, insight_type: insightType }, { headers: getAuthHeader() }),

  // Translate
  translate: (text, target) => 
    axios.post(`${API}/translate`, { text, target }, { headers: getAuthHeader() }),

  // Tiers
  getTiers: () =>
    axios.get(`${API}/tiers`),

  // Labs
  getLabs: () =>
    axios.get(`${API}/labs`, { headers: getAuthHeader() }),
  
  getLab: (labId) =>
    axios.get(`${API}/labs/${labId}`, { headers: getAuthHeader() }),
  
  getLabDetail: (labId) =>
    axios.get(`${API}/labs/${labId}`, { headers: getAuthHeader() }),
  
  submitLabStep: (labId, stepId, response) =>
    axios.post(`${API}/labs/${labId}/submit`, { lab_id: labId, step_id: stepId, response }, { headers: getAuthHeader() }),
  
  saveLabProgress: (labId, progress) =>
    axios.post(`${API}/labs/${labId}/progress`, progress, { headers: getAuthHeader() }),
  
  getLabStepFeedback: (labId, stepId, response) =>
    axios.post(`${API}/labs/${labId}/feedback`, { step_id: stepId, response }, { headers: getAuthHeader(), timeout: 30000 }),
  
  completeLab: (labId, data) =>
    axios.post(`${API}/labs/${labId}/complete`, data || {}, { headers: getAuthHeader() }),
  
  // Certification
  getCertificationProgress: () =>
    axios.get(`${API}/certification/progress`, { headers: getAuthHeader() }),
  
  claimCertificate: () =>
    axios.post(`${API}/certification/claim`, {}, { headers: getAuthHeader() }),
  
  downloadCertificate: (certificateId) =>
    axios.get(`${API}/certification/download/${certificateId}`, { 
      headers: getAuthHeader(),
      responseType: 'text'
    }),

  // Minnow prompts
  getMinnowPrompts: () =>
    axios.get(`${API}/minnow/prompts`, { headers: getAuthHeader() }),

  // Lesson Chat
  getLessonChatHistory: (lessonId) =>
    axios.get(`${API}/lessons/${lessonId}/chat/history`, { headers: getAuthHeader() }),
  
  sendLessonChatMessage: (lessonId, content) =>
    axios.post(`${API}/lessons/${lessonId}/chat`, { lesson_id: lessonId, content }, { headers: getAuthHeader() }),

  clearLessonChatHistory: (lessonId) =>
    axios.delete(`${API}/lessons/${lessonId}/chat/history`, { headers: getAuthHeader() }),

  // Weekly Focus Update
  updateWeeklyFocus: (action, itemType, itemId, reason) =>
    axios.post(`${API}/weekly-focus/update`, { action, item_type: itemType, item_id: itemId, reason }, { headers: getAuthHeader() }),
  
  // Pathways
  getPathways: () =>
    axios.get(`${API}/pathways`, { headers: getAuthHeader() }),

  // User Settings & Tracking
  getLastLesson: () =>
    axios.get(`${API}/user/last-lesson`, { headers: getAuthHeader() }),
  
  updateLastLesson: (moduleId, lessonId, lessonTitle) =>
    axios.post(`${API}/user/last-lesson`, { module_id: moduleId, lesson_id: lessonId, lesson_title: lessonTitle }, { headers: getAuthHeader() }),
  
  getUserSettings: () =>
    axios.get(`${API}/user/settings`, { headers: getAuthHeader() }),
  
  updateUserSettings: (settings) =>
    axios.put(`${API}/user/settings`, settings, { headers: getAuthHeader() }),
  
  // Regional Rooms
  getRegionalRooms: () =>
    axios.get(`${API}/regional-rooms`, { headers: getAuthHeader() }),

  // Payments
  getPaymentConfig: () =>
    axios.get(`${API}/payments/config`, { headers: getAuthHeader() }),
  
  createCheckout: (lookupKey, originUrl) =>
    axios.post(`${API}/payments/checkout`, { lookup_key: lookupKey, origin_url: originUrl }, { headers: getAuthHeader() }),
  
  getPaymentStatus: (sessionId) =>
    axios.get(`${API}/payments/status/${sessionId}`, { headers: getAuthHeader() }),
  
  getSubscriptionStatus: () =>
    axios.get(`${API}/payments/subscription`, { headers: getAuthHeader() }),
  
  cancelSubscription: () =>
    axios.post(`${API}/payments/cancel-subscription`, {}, { headers: getAuthHeader() }),
  
  createCustomerPortal: () =>
    axios.post(`${API}/payments/portal`, {}, { headers: getAuthHeader() }),

  // Analytics
  getProgressAnalytics: () =>
    axios.get(`${API}/analytics/progress`, { headers: getAuthHeader() }),
  
  trackActivity: () =>
    axios.post(`${API}/analytics/track`, {}, { headers: getAuthHeader() }),

  // Badges
  getAllBadges: () =>
    axios.get(`${API}/badges`, { headers: getAuthHeader() }),
  
  getUserBadges: () =>
    axios.get(`${API}/user/badges`, { headers: getAuthHeader() }),

  // Account Deletion
  deleteAccount: () =>
    axios.delete(`${API}/user/account`, { headers: getAuthHeader() }),

  // Text-to-Speech
  generateTTS: (text, voice = 'nova', speed = 1.0) =>
    axios.post(`${API}/tts/generate`, null, { 
      headers: getAuthHeader(),
      params: { text, voice, speed }
    }),
  
  getTTSAudioUrl: (audioId) =>
    `${API}/tts/audio/${audioId}`,
};
